/**
 * Query-string helpers so /assessment/:link/take and /results survive refresh (no React Router state).
 */

export function buildAssessmentFlowSearch(params) {
  const {
    email = '',
    name = '',
    screeningId = '',
    returnUrl = '',
    hasInterview = false,
    interviewSharingLink = '',
  } = params || {};
  const q = new URLSearchParams();
  const em = String(email || '').trim();
  const nm = String(name || '').trim();
  const sid = String(screeningId || '').trim();
  const iv = String(interviewSharingLink || '').trim();
  if (em) q.set('email', em);
  if (nm) q.set('name', nm);
  if (sid) q.set('screeningId', sid);
  if (returnUrl) q.set('returnUrl', returnUrl);
  if (hasInterview) q.set('hasInterview', 'true');
  if (iv) q.set('interviewSharingLink', iv);
  const s = q.toString();
  return s ? `?${s}` : '';
}

const DRAFT_PREFIX = 'tc_assessment_take_draft_v1:';

export function assessmentTakeDraftKey(sharingLink, emailLower) {
  if (!sharingLink || !emailLower) return null;
  return `${DRAFT_PREFIX}${sharingLink}|${emailLower}`;
}

export function loadAssessmentTakeDraft(sharingLink, emailLower) {
  const key = assessmentTakeDraftKey(sharingLink, emailLower);
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveAssessmentTakeDraft(sharingLink, emailLower, draft) {
  const key = assessmentTakeDraftKey(sharingLink, emailLower);
  if (!key || typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    /* ignore */
  }
}

export function clearAssessmentTakeDraft(sharingLink, emailLower) {
  const key = assessmentTakeDraftKey(sharingLink, emailLower);
  if (!key || typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export const AI_INTERVIEW_TAKE_STORAGE_KEY = 'tc_ai_iv_take_state_v1';
const AI_INTERVIEW_TAKE_MIN_KEY = 'tc_ai_iv_take_min_v1';

function slimInterviewForStorage(interview) {
  if (!interview || typeof interview !== 'object') return interview;
  return {
    _id: interview._id,
    type: interview.type,
    title: interview.title,
  };
}

function slimInterviewTakeStateForStorage(state) {
  if (!state || typeof state !== 'object') return state;
  return { ...state, interview: slimInterviewForStorage(state.interview) };
}

/** Persist name/email before take so refresh can restart session/start if full state save failed. */
export function saveAiInterviewTakeMinimalBackup(sharingLink, partial) {
  if (!sharingLink || typeof window === 'undefined') return;
  const { email = '', name = '', screeningId = '' } = partial || {};
  const em = String(email).trim();
  const nm = String(name).trim();
  if (!em || !nm) return;
  try {
    sessionStorage.setItem(
      AI_INTERVIEW_TAKE_MIN_KEY,
      JSON.stringify({
        sharingLink,
        email: em,
        name: nm,
        screeningId: String(screeningId || '').trim(),
        savedAt: Date.now(),
      })
    );
  } catch {
    /* ignore */
  }
}

export function loadAiInterviewTakeMinimalBackup(sharingLink) {
  if (!sharingLink || typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(AI_INTERVIEW_TAKE_MIN_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || p.sharingLink !== sharingLink || !p.email || !p.name) return null;
    return p;
  } catch {
    return null;
  }
}

/**
 * Resolve take-page state after refresh: router state → full sessionStorage → minimal backup → URL query only.
 */
export function resolveAiInterviewTakeState(sharingLink, routerState, searchParams) {
  const qe = (searchParams?.get?.('email') || '').trim();
  const qn = (searchParams?.get?.('name') || '').trim();
  const qsId = (searchParams?.get?.('screeningId') || '').trim();
  const enrich = (base) => {
    if (!base) return null;
    const email = (base.email || qe || '').trim();
    const name = (base.name || qn || '').trim();
    if (!email || !name) return null;
    return {
      ...base,
      email,
      name,
      screeningId: base.screeningId || qsId || undefined,
      sharingLink: base.sharingLink || sharingLink,
    };
  };

  if (
    routerState &&
    (routerState.sessionId || routerState.preInitializedLivekit)
  ) {
    const fromRouter = enrich(routerState);
    if (fromRouter) return fromRouter;
  }

  const full = loadAiInterviewTakeState(sharingLink);
  if (full?.sessionId) return enrich(full);

  const min = loadAiInterviewTakeMinimalBackup(sharingLink);
  if (min) return enrich({ ...min, sharingLink });

  if (qe && qn && sharingLink) {
    return enrich({ sharingLink, email: qe, name: qn, screeningId: qsId || undefined });
  }
  return null;
}

export function saveAiInterviewTakeState(sharingLink, state) {
  if (!sharingLink || !state?.sessionId || typeof window === 'undefined') return;
  const slim = slimInterviewTakeStateForStorage(state);
  const payload = { sharingLink, savedAt: Date.now(), state: slim };
  try {
    sessionStorage.setItem(AI_INTERVIEW_TAKE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    try {
      const minimal = {
        sessionId: slim.sessionId,
        sessionToken: slim.sessionToken,
        sharingLink: slim.sharingLink || sharingLink,
        name: slim.name,
        email: slim.email,
        screeningId: slim.screeningId,
        hasAssessment: slim.hasAssessment,
        deliveryMode: slim.deliveryMode,
        preInitializedLivekit: slim.preInitializedLivekit,
        livekitUrl: slim.livekitUrl,
        livekitToken: slim.livekitToken,
        agentAlreadyReady: slim.agentAlreadyReady,
        interview: slimInterviewForStorage(slim.interview),
      };
      sessionStorage.setItem(
        AI_INTERVIEW_TAKE_STORAGE_KEY,
        JSON.stringify({ sharingLink, savedAt: Date.now(), state: minimal })
      );
    } catch {
      /* ignore */
    }
  }
}

export function loadAiInterviewTakeState(sharingLink) {
  if (!sharingLink || typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(AI_INTERVIEW_TAKE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.sharingLink !== sharingLink || !parsed.state) return null;
    return parsed.state;
  } catch {
    return null;
  }
}

export function clearAiInterviewTakeState() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(AI_INTERVIEW_TAKE_STORAGE_KEY);
    sessionStorage.removeItem(AI_INTERVIEW_TAKE_MIN_KEY);
  } catch {
    /* ignore */
  }
}
